'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface PricingTier {
  minQuantity: number;
  pricePerUnit: number;
  discount?: number;
}

interface PurchaseOption {
  type: 'pack' | 'box';
  label: string;
  unitsPerOption: number;
  basePrice: number;
  pricingTiers?: PricingTier[];
}

interface Product {
  id: string;
  name: string;
  category: string;
  image: string;
  alt: string;
  purchaseOptions: PurchaseOption[];
  minPackQuantity: number;
  minBoxQuantity: number;
  inStock: boolean;
}

interface ProductCardProps {
  product: Product;
  onAddToCart: (productId: string, quantity: number, optionType: 'pack' | 'box') => void;
}

const ProductCard = ({ product, onAddToCart }: ProductCardProps) => {
  const [selectedOption, setSelectedOption] = useState<'pack' | 'box'>('pack');
  const getMinQuantity = (option: 'pack' | 'box') =>
    option === 'pack' ? product.minPackQuantity : product.minBoxQuantity;
  const [quantity, setQuantity] = useState(getMinQuantity('pack'));
  const [isAdding, setIsAdding] = useState(false);

  const currentOption = product.purchaseOptions.find((opt) => opt.type === selectedOption);

  const calculatePrice = () => {
    if (!currentOption) return 0;

    let pricePerUnit = currentOption.basePrice;

    if (currentOption.pricingTiers) {
      const applicableTier = [...currentOption.pricingTiers]
        .reverse()
        .find((tier) => quantity >= tier.minQuantity);

      if (applicableTier) {
        pricePerUnit = applicableTier.pricePerUnit;
      }
    }

    return pricePerUnit * quantity;
  };

  const handleQuantityChange = (newQuantity: number) => {
    const minQty = getMinQuantity(selectedOption);
    if (newQuantity >= minQty) {
      setQuantity(newQuantity);
    }
  };

  const handleDecrement = () => {
    handleQuantityChange(quantity - 1);
  };

  const handleIncrement = () => {
    handleQuantityChange(quantity + 1);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const minQty = getMinQuantity(selectedOption);
    const value = parseInt(e.target.value) || minQty;
    handleQuantityChange(value);
  };

  const handleAddToCart = () => {
    setIsAdding(true);
    onAddToCart(product.id, quantity, selectedOption);
    setTimeout(() => setIsAdding(false), 300);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ru-KZ', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className="group flex flex-col overflow-hidden rounded-lg bg-card shadow-sm transition-all duration-250 hover:-translate-y-1 hover:shadow-md">
      <div className="relative aspect-square overflow-hidden">
        <AppImage
          src={product.image}
          alt={product.alt}
          className="h-full w-full object-cover transition-transform duration-250 group-hover:scale-105"
        />
        {!product.inStock && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80">
            <span className="rounded-md bg-error px-4 py-2 font-heading text-sm font-medium text-error-foreground">
              Нет в наличии
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col p-4">
        <h3 className="mb-2 font-heading text-base font-medium text-text-primary line-clamp-2">
          {product.name}
        </h3>

        <div className="mb-3 flex gap-2">
          {product.purchaseOptions.map((option) => (
            <button
              key={option.type}
              onClick={() => {
                setSelectedOption(option.type);
                setQuantity(getMinQuantity(option.type));
              }}
              disabled={!product.inStock}
              className={`flex-1 rounded-md px-3 py-2 font-caption text-sm font-medium transition-all duration-250 disabled:cursor-not-allowed disabled:opacity-50 ${
                selectedOption === option.type
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-text-primary hover:bg-muted/80'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="mb-3 border-t border-border pt-3">
          <div className="mb-2 flex items-baseline justify-between">
            <span className="font-caption text-sm text-text-secondary">Цена:</span>
            <span className="font-heading text-xl font-semibold text-primary">
              {formatPrice(calculatePrice())} ₸
            </span>
          </div>
          {currentOption && (
            <p className="font-caption text-xs text-text-secondary">
              {formatPrice(currentOption.basePrice)} ₸ за {currentOption.label.toLowerCase()}
            </p>
          )}
        </div>

        {currentOption?.pricingTiers && currentOption.pricingTiers.length > 0 && (
          <div className="mb-3 rounded-md bg-muted p-2">
            <p className="mb-1 font-caption text-xs font-medium text-text-primary">
              Скидки при заказе:
            </p>
            {currentOption.pricingTiers.map((tier, index) => (
              <p key={index} className="font-caption text-xs text-text-secondary">
                от {tier.minQuantity} шт: {formatPrice(tier.pricePerUnit)} ₸
                {tier.discount && ` (-${tier.discount}%)`}
              </p>
            ))}
          </div>
        )}

        <div className="mb-3 border-t border-border pt-3">
          <label className="mb-2 block font-caption text-sm text-text-secondary">
            Количество (мин. {getMinQuantity(selectedOption)}):
          </label>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDecrement}
              disabled={quantity <= getMinQuantity(selectedOption) || !product.inStock}
              className="flex h-10 w-10 items-center justify-center rounded-md bg-muted text-text-primary transition-all duration-250 hover:bg-muted/80 disabled:cursor-not-allowed disabled:opacity-50 active:scale-95"
              aria-label="Уменьшить количество"
            >
              <Icon name="MinusIcon" size={20} />
            </button>
            <input
              type="number"
              value={quantity}
              onChange={handleInputChange}
              disabled={!product.inStock}
              min={getMinQuantity(selectedOption)}
              className="h-10 flex-1 rounded-md border border-input bg-background px-3 text-center font-mono text-base text-text-primary focus:outline-none focus:ring-2 focus:ring-accent disabled:cursor-not-allowed disabled:opacity-50"
            />
            <button
              onClick={handleIncrement}
              disabled={!product.inStock}
              className="flex h-10 w-10 items-center justify-center rounded-md bg-muted text-text-primary transition-all duration-250 hover:bg-muted/80 disabled:cursor-not-allowed disabled:opacity-50 active:scale-95"
              aria-label="Увеличить количество"
            >
              <Icon name="PlusIcon" size={20} />
            </button>
          </div>
        </div>

        <button
          onClick={handleAddToCart}
          disabled={!product.inStock || isAdding}
          className={`mt-auto flex items-center justify-center gap-2 rounded-md bg-accent px-4 py-3 font-heading text-sm font-medium text-accent-foreground transition-all duration-250 hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50 active:scale-95 ${
            isAdding ? 'scale-95' : ''
          }`}
        >
          <Icon name="ShoppingCartIcon" size={20} />
          <span>{isAdding ? 'Добавлено!' : 'Добавить в заявку'}</span>
        </button>
      </div>
    </div>
  );
};

export default ProductCard;
